import { z } from 'zod';
import { addCalendarDays, daysBetweenInclusive } from './absence.js';
import { StaffLevel } from './admin-users.js';
import { CalendarDate } from './project.js';
import { TeamProjectsQuery } from './team-projects.js';
/**
 * Hiệu suất đội v2 — APM-TPR2-a, `docs/plan-team-performance-v2.md`.
 *
 * Chỉ số **đếm được** cho từng người trong kỳ và kỳ liền trước, thay cho bảng
 * Tốc độ / Đúng hạn / Làm lại / Review (người dùng 2026-10-12: *"chưa thấy nó có
 * ý nghĩa nhiều"*).
 *
 * ## ⚠️ Không có điểm tổng, dưới BẤT KỲ tên gọi nào
 *
 * Kế thừa nguyên luật của `PerformanceRosterRow`: không `score`, `overall`,
 * `rating`, `rank`, `percentile`, `band`, `grade`. Bảng này còn dễ bị biến
 * thành bảng xếp hạng hơn bảng cũ — toàn số đếm, cộng lại là ra một "điểm" — nên
 * schema không mang trường nào như thế, và web sắp mặc định theo tên.
 */
/** Cùng luật kỳ với "Nhân sự × dự án": hai tab dùng **một** bộ chọn kỳ. */
export const TeamPerformanceQuery = TeamProjectsQuery;
/** Trần việc xong đem đo bằng event mỗi kỳ — vượt thì đo phần mới nhất và bật `truncated`. */
export const TEAM_PERFORMANCE_MAX_DONE_TASKS = 5000;
/**
 * Kỳ liền trước để so — plan §4. Một định nghĩa cho cả API (tính số) lẫn web
 * (nhãn "So với …").
 *
 * - `from` là ngày 1 ⇒ lùi đúng **một tháng lịch** cả hai đầu. `to` là ngày
 *   cuối tháng thì ra ngày cuối của tháng trước (tháng trọn so với tháng trọn:
 *   01/09–30/09 so với 01/08–31/08); không thì kẹp vào ngày cuối tháng.
 *   "Tháng này" 01/10–12/10 so với 01/09–12/09.
 * - Kỳ ≤ 7 ngày ⇒ lùi **7 ngày**, để trùng thứ trong tuần: "Tuần này" T2–T5 so
 *   với T2–T5 tuần trước, không phải T5–CN (cuối tuần không ai làm).
 * - Còn lại ⇒ lùi L ngày, L = số ngày của kỳ.
 */
export function previousRange(from, to) {
    if (from.endsWith('-01'))
        return { from: monthBack(from), to: monthBack(to) };
    const days = daysBetweenInclusive(from, to);
    const shift = days <= 7 ? 7 : days;
    return { from: addCalendarDays(from, -shift), to: addCalendarDays(to, -shift) };
}
function monthBack(date) {
    const [year, month, day] = date.split('-').map(Number);
    const lastOf = (y, m0) => new Date(Date.UTC(y, m0 + 1, 0)).getUTCDate();
    const target = new Date(Date.UTC(year, month - 2, 1));
    const targetLast = lastOf(target.getUTCFullYear(), target.getUTCMonth());
    // Ngày cuối tháng ⇒ ngày cuối tháng trước: tháng trọn so với tháng trọn.
    target.setUTCDate(day === lastOf(year, month - 1) ? targetLast : Math.min(day, targetLast));
    return target.toISOString().slice(0, 10);
}
/**
 * Chỉ số của một người (hoặc cả đội) trong một kỳ — plan §3.1.
 *
 * Mang cả **tử số lẫn mẫu số** của các tỷ lệ (`onTimeSamples`, `reopened`,
 * `cycleSamples`): tỷ lệ đứng một mình không phân biệt "100% trên 2 việc" với
 * "100% trên 40 việc", và tổng của đội phải cộng từ số đếm, không phải trung
 * bình các tỷ lệ.
 */
export const TeamPerformanceMetrics = z.object({
    /** Việc xong trong kỳ (hiện vẫn xong) — **bằng** `tasksDone` của `/team-projects` cùng kỳ. */
    tasksDone: z.number().int().min(0),
    /** Mục checklist người này tick xong trong kỳ (trạng thái cuối trong kỳ, không tính hệ thống). */
    checklistDone: z.number().int().min(0),
    /** Σ `story_points` đã khai của việc xong. */
    spDeclared: z.number().int().min(0),
    /** Σ SP ước lượng của việc xong **không** có điểm (AI, không thì công thức) — plan §5. */
    spEstimated: z.number().int().min(0),
    /** Phần của `spEstimated` đến từ gợi ý AI. */
    spEstimatedAi: z.number().int().min(0),
    /** Việc xong không có điểm và không ước lượng được. */
    spUnestimated: z.number().int().min(0),
    /** Việc xong không có điểm và **chưa** có gợi ý AI — số việc nút "Ước lượng bằng AI" còn làm được (APM-TPR2-c). */
    spAiMissing: z.number().int().min(0),
    /** Việc xong **có hạn** (lúc xong) — mẫu số của `onTimePct`. */
    onTimeSamples: z.number().int().min(0),
    /** `null` khi không việc xong nào có hạn. */
    onTimePct: z.number().int().min(0).max(100).nullable(),
    /** Việc xong sau hạn có hiệu lực lúc xong. */
    lateDone: z.number().int().min(0),
    /**
     * Số lần **dời hạn ra sau** của các việc xong trong kỳ, tính tới lúc xong — APM-TPR2-d.
     * Một lần = một `task.dates_changed` có hạn trước đó và hạn mới muộn hơn; đặt hạn
     * lần đầu, kéo hạn sớm lên hay xoá hạn không tính. Đi cạnh `onTimePct`: % đúng hạn
     * so với hạn **lúc xong**, nên dời hạn trước khi xong là cách "đúng hạn" không tốn gì.
     */
    dueExtensions: z.number().int().min(0),
    /** Việc xong có ít nhất một lần dời hạn — để biết `dueExtensions` dồn vào mấy việc. */
    dueExtendedTasks: z.number().int().min(0),
    /** Việc đang giao, chưa xong tại cuối kỳ, hạn trước ngày `to` — plan §3.4. */
    overdueOpen: z.number().int().min(0),
    /** Việc xong từng bị mở lại sau khi đã xong. */
    reopened: z.number().int().min(0),
    /** `reopened ÷ tasksDone`; `null` khi không xong việc nào. */
    reopenPct: z.number().int().min(0).max(100).nullable(),
    /** Trung vị (xong − bắt đầu thật), ngày lịch, 1 chữ số thập phân. */
    cycleDays: z.number().min(0).nullable(),
    cycleSamples: z.number().int().min(0),
    /** Giờ bấm trong kỳ, cắt theo kỳ. */
    hours: z.number().min(0),
    /** Σ giờ thật ÷ Σ ước lượng của việc xong có cả hai. 1 = khớp. */
    estimateRatio: z.number().min(0).nullable(),
});
/** Tổng của đội trong một kỳ — plan §3.2. */
export const TeamPerformanceTotals = TeamPerformanceMetrics.extend({
    /** Giờ theo lịch gốc trong kỳ, cộng cả đội — mẫu số của mức sử dụng. */
    scheduledHours: z.number().min(0),
    /** `hours ÷ scheduledHours`; `null` khi không ai có lịch. */
    utilizationPct: z.number().int().min(0).nullable(),
    /** Người có task xong, giờ bấm hoặc mục checklist trong kỳ. */
    activePeople: z.number().int().min(0),
    /** Người có ít nhất một việc quá hạn tại cuối kỳ. */
    peopleWithOverdue: z.number().int().min(0),
});
export const TeamPerformancePerson = z.object({
    userId: z.string(),
    name: z.string(),
    level: StaffLevel.nullable(),
    current: TeamPerformanceMetrics,
    previous: TeamPerformanceMetrics,
    /** Việc đang ở nhóm `in_progress` **lúc đọc** — không có kỳ trước. */
    wip: z.number().int().min(0),
    /** Tải tuần này (như cột của Nhân sự × dự án); `null` khi nghỉ cả tuần. */
    thisWeekPct: z.number().int().min(0).nullable(),
});
/**
 * ⚠️ Không nhận `asOf` — ngoại lệ 2 của luật #7: `from`/`to` là trục thời gian
 * riêng; WIP và tải tuần này luôn tính tới hôm nay.
 */
export const TeamPerformanceReport = z.object({
    from: CalendarDate,
    to: CalendarDate,
    previous: z.object({ from: CalendarDate, to: CalendarDate }),
    timezone: z.string(),
    capturedAt: z.string(),
    scope: z.enum(['org', 'chain']),
    /** Một trong hai kỳ chạm `TEAM_PERFORMANCE_MAX_DONE_TASKS` — đúng hạn / mở lại chỉ đo phần mới nhất. */
    truncated: z.boolean(),
    people: z.array(TeamPerformancePerson),
    team: z.object({
        current: TeamPerformanceTotals,
        previous: TeamPerformanceTotals,
        people: z.number().int().min(0),
        wip: z.number().int().min(0),
        /** Người có tải tuần này > 100%. */
        overloaded: z.number().int().min(0),
    }),
});
/**
 * Lời trả lời của skill `summarize_team_performance` — APM-CHAT2-c. Chỉ là lời văn: mọi con
 * số phải có sẵn trong bản số liệu đội mà server đã tính (`finalize` kiểm).
 */
export const TeamPerformanceBrief = z.object({
    answer: z.string().trim().min(1).max(2000),
});
/**
 * Một dự án trong "Hiệu suất của tôi" — APM-PERFME-a. Cùng bộ chỉ số với một
 * dòng của bảng đội, nhưng hàng là **dự án** của chính người xem.
 */
export const MyPerformanceProject = z.object({
    projectId: z.string(),
    key: z.string(),
    name: z.string(),
    current: TeamPerformanceMetrics,
    previous: TeamPerformanceMetrics,
    /** Việc của tôi đang ở nhóm `in_progress` ở dự án này **lúc đọc**. */
    wip: z.number().int().min(0),
});
/**
 * `GET /performance/me/report?from=&to=` — báo cáo hiệu suất **của chính mình**
 * theo kỳ, cùng phép tính với `/team-performance` (APM-PERFME-a).
 *
 * Không quyền, không ghi sổ truy cập: dữ liệu của chính người đọc (spec mục 10).
 */
export const MyPerformanceReport = z.object({
    from: CalendarDate,
    to: CalendarDate,
    previous: z.object({ from: CalendarDate, to: CalendarDate }),
    timezone: z.string(),
    capturedAt: z.string(),
    truncated: z.boolean(),
    me: z.object({
        current: TeamPerformanceTotals,
        previous: TeamPerformanceTotals,
        wip: z.number().int().min(0),
        /** Tải tuần này; `null` khi nghỉ cả tuần. */
        thisWeekPct: z.number().int().min(0).nullable(),
    }),
    /** Dự án có số trong kỳ này hoặc kỳ trước, hoặc đang có việc dở — theo tên. */
    projects: z.array(MyPerformanceProject),
});
/** Kỳ dài hơn bấy nhiêu ngày thì mỗi cột heatmap là một **tuần** — APM-PERFME-b. */
export const ACTIVITY_DAILY_MAX_DAYS = 62;
/**
 * Một hàng của "Mức độ cập nhật" theo kỳ — APM-PERFME-b. Ở tab đội hàng là
 * **người**, ở tab của tôi hàng là **dự án**.
 */
export const PeriodActivityRow = z.object({
    id: z.string(),
    label: z.string(),
    /** Số lần cập nhật mỗi cột, cùng thứ tự với `PeriodActivityReport.columns`. */
    counts: z.array(z.number().int().min(0)),
    total: z.number().int().min(0),
});
/**
 * `GET /team-activity` và `GET /performance/me/activity` — số event do **người**
 * tạo (kéo thẻ, sửa việc, bình luận…) trên mọi dự án không demo, theo ngày của
 * tổ chức. Đo **thói quen cập nhật**, không đo năng suất.
 *
 * ⚠️ Không nhận `asOf` — ngoại lệ 2 của luật #7: `from`/`to` là trục thời gian.
 */
export const PeriodActivityReport = z.object({
    from: CalendarDate,
    to: CalendarDate,
    /** `day` khi kỳ ≤ `ACTIVITY_DAILY_MAX_DAYS` ngày, không thì `week`. */
    bucket: z.enum(['day', 'week']),
    /** Ngày đầu của mỗi cột (với `week`: thứ Hai, cột đầu cắt ở `from`). */
    columns: z.array(CalendarDate),
    rows: z.array(PeriodActivityRow),
    /** Tổng mỗi cột trên mọi hàng. */
    totals: z.array(z.number().int().min(0)),
    /** Ô lớn nhất trong `rows` — để chuẩn hoá thang màu. */
    peak: z.number().int().min(0),
});
/**
 * Cột của heatmap theo kỳ: từng ngày, hoặc từng tuần (thứ Hai) khi kỳ dài —
 * 366 cột một ngày là một dải không đọc được.
 */
export function activityColumns(from, to) {
    const days = daysBetweenInclusive(from, to);
    if (days <= ACTIVITY_DAILY_MAX_DAYS) {
        return {
            bucket: 'day',
            columns: Array.from({ length: days }, (_, i) => addCalendarDays(from, i)),
        };
    }
    const columns = [from];
    let next = addCalendarDays(weekStartOf(from), 7);
    while (next <= to) {
        columns.push(next);
        next = addCalendarDays(next, 7);
    }
    return { bucket: 'week', columns };
}
/** Chỉ số cột chứa `day`; `-1` khi ngoài kỳ. */
export function activityColumnOf(day, from, to, bucket) {
    if (day < from || day > to)
        return -1;
    if (bucket === 'day')
        return daysBetweenInclusive(from, day) - 1;
    return Math.floor((daysBetweenInclusive(weekStartOf(from), day) - 1) / 7);
}
/** Thứ Hai của tuần chứa `day`. */
function weekStartOf(day) {
    const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
    return addCalendarDays(day, -((weekday + 6) % 7));
}
/** Mỗi lượt bấm "Ước lượng SP bằng AI" xếp tối đa bấy nhiêu việc — dưới trần 20 lượt/phút, 60 lượt/giờ của một skill. */
export const TEAM_PERFORMANCE_AI_SP_BATCH = 20;
/** Kết quả nút "Ước lượng SP còn thiếu bằng AI" — APM-TPR2-c. */
export const EstimateMissingSpResult = z.object({
    /** Việc đã xếp vào job lượt này. */
    queued: z.number().int().min(0),
    /** Việc thiếu điểm nhưng chưa đủ chi tiết để AI ước lượng (không checklist, mô tả ≤ 50 từ) — công thức lo. */
    notDetailed: z.number().int().min(0),
    /** Việc đủ điều kiện còn lại sau lượt này (vượt `TEAM_PERFORMANCE_AI_SP_BATCH`). */
    remaining: z.number().int().min(0),
});
//# sourceMappingURL=team-performance.js.map